import { AurigaRumConfig, generateRumConfig } from '@utils/rum/cloudWatchRUMHelpers';
import { AwsRum } from 'aws-rum-web';
import { isNil } from 'lodash';
import {
  RumBusinessEventAction,
  RumBusinessEventPayloadBase,
  RumBusinessEventPayloadOnLoad,
  RumBusinessEventPayloadType,
} from 'types/RUM.types';

const BUSINESS_EVENT_TAG = 'gft-auriga-deckprep-business-event';

/**
 * RUM Metrics Logger
 *
 * Centralized logger for CloudWatch RUM custom metrics recording
 */
class RumMetricsLogger {
  private awsRum: AwsRum | null = null;

  private rumConfig: AurigaRumConfig | null = null;

  private isInitialized = false;

  private loadStartTimes: { [key: string]: number } = {};

  /**
   * Constructs a rum instance using an optionally provided config
   * @param rumConfig - Optional RUM config to use. If not provided, the default generated Config will be used
   */
  constructor(rumConfig?: AurigaRumConfig) {
    this.rumConfig = rumConfig || generateRumConfig();
    this.initializeRUM();
  }

  /**
   * Initializes the RUM client for use throughout the application
   */
  initializeRUM = () => {
    if (this.isInitialized) {
      console.debug('RUM Metrics Logger already initialized');
      return;
    }

    try {
      if (isNil(this.rumConfig)) {
        console.error('RUM configuration is missing');
        return;
      }

      // Create the AWS RUM client instance
      this.awsRum = new AwsRum(
        this.rumConfig.applicationId,
        this.rumConfig.applicationVersion,
        this.rumConfig.applicationRegion,
        this.rumConfig.rumConfig,
      );

      // Enable and set initialized
      this.awsRum.enable();
      this.isInitialized = true;
      this.awsRum.recordEvent('custom.rum_initialized', { type: 'rum_initialized', timestamp: Date.now() });
      console.debug('RUM Metrics Logger initialized successfully');
    } catch (error) {
      console.error('RUM Metrics Logger initialization error:', error);
      this.awsRum = null;
      this.isInitialized = false;
    }
  };

  /**
   * Check if RUM is initialized and ready to use
   */
  isReady = (): boolean => {
    return this.isInitialized && !isNil(this.rumConfig) && !isNil(this.awsRum);
  };

  /**
   * Get the RUM direct instance (primarily for debugging and should be limited use).
   * - Prefer use/access through rumMetricsLogger below
   */
  getRumInstance = (): AwsRum | null => {
    return this.awsRum;
  };

  /**
   * Record API latency metrics as needed
   * @param serviceName - The name of the Service being called (e.g. DimSum, DeckManagement)
   * @param apiOperation - The name of the api operation being called (e.g. SaveDeck, GetPage, etc)
   * @param startTime - The start time of the API call
   * @param success - Whether the API call was successful
   * @param operationType - Optional React Query kind ('query' | 'mutation'); emitted as `operation_type`
   */
  recordApiLatency = (
    serviceName: string,
    apiOperation: string,
    startTime: number,
    success: boolean = true,
    operationType?: 'query' | 'mutation',
  ) => {
    if (!this.isReady()) {
      console.debug('RUM: No RUM instance available, skipping');
      return;
    }

    // Calculate duration of API call
    const duration = Date.now() - startTime;

    // Debugging log for development environment testing
    if (process.env.NODE_ENV === 'development') {
      console.debug(`RUM: ${serviceName}.${apiOperation} - ${duration}ms - ${success ? 'SUCCESS' : 'FAILED'}`);
    }

    this.awsRum?.recordEvent('custom.api_request_latency', {
      service_name: serviceName,
      api_operation: apiOperation,
      duration_ms: duration,
      success: success.toString(),
      // Distinguishes React Query queries from mutations on the dashboard; omitted by callers that
      // don't classify (e.g. legacy direct callers).
      ...(operationType ? { operation_type: operationType } : {}),
    });
  };

  recordUserInteraction = async (action: string, component: string) => {
    if (!this.isReady()) {
      console.debug('RUM: No RUM instance available, skipping');
      return;
    }

    this.awsRum?.recordEvent('custom.user_interaction', {
      action,
      component,
      timestamp: new Date().toISOString(),
    });
  };

  /**
   * Marks the beginning of a page load timing measurement for the given route path.
   * Called by RouteTracker (in AurigaLayout) on every route change.
   * @param path - The route path to track (e.g. `location.pathname + location.search`)
   */
  startOnLoad = (path: string) => {
    this.loadStartTimes[path] = Date.now();
  };

  /**
   * Records a RUM business event measuring the duration from {@link startOnLoad} to now for the given route path.
   * Should be called once per route when the component considers its content fully loaded.
   * Consumed via the {@link useRecordLoadComplete} hook in each route component.
   *
   * If no matching {@link startOnLoad} entry exists for the path, the call is a no-op.
   *
   * @param path - The route path that finished loading (must match the path passed to startOnLoad)
   * @param eventMetadata - Optional contextual fields (e.g. userAlias) merged into the event payload.
   *   Spread before the computed fields so that location, value, and duration cannot be overridden.
   */
  recordLoadCompleteEvent = (
    path: string,
    eventMetadata: Partial<Omit<RumBusinessEventPayloadOnLoad, 'location' | 'action'>> = {},
  ) => {
    if (this.loadStartTimes[path]) {
      const { [path]: startTime, ...newStartTimes } = this.loadStartTimes;
      // eventMetadata is spread first so computed fields (location, value, duration) always take precedence
      this.recordBusinessEvent(RumBusinessEventAction.LOAD, {
        ...eventMetadata,
        location: window.location.origin + path,
        value: 1,
        duration: Date.now() - startTime,
      });
      this.loadStartTimes = newStartTimes;
    }
  };

  /**
   * Records a RUM business event with the given action and payload.
   * @param action - The action type of the business event
   * @param payload - The payload to send with the business event
   */
  recordBusinessEvent = <TAction extends RumBusinessEventAction>(
    action: TAction,
    payload: Omit<RumBusinessEventPayloadType & { action: TAction }, 'action'>,
  ) => {
    if (!this.isReady()) {
      console.debug('RUM: No RUM instance available, skipping');
      return;
    }

    const payloadDefaults: Omit<RumBusinessEventPayloadBase, 'action'> = {
      // `timezone` requested by Metrics One team
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const finalPayload = { ...payloadDefaults, ...payload, action };
    console.debug('[RUM][recordBusinessEvent] emitting: ', action, finalPayload);
    this.awsRum?.recordEvent(BUSINESS_EVENT_TAG, finalPayload);
  };
}

export default RumMetricsLogger;
